import { api } from '@/lib/api';
import { backendOriginWithoutApi, config, normalizeUploadsUrlPath } from '@/config';

export type V2Pagination = {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
};

/** Corps `data` d’une réponse API Lyrra. */
export function unwrapV2Data<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

export function unwrapV2Paginated<T>(raw: unknown): { items: T[]; pagination: V2Pagination | null } {
  if (!raw || typeof raw !== 'object') return { items: [], pagination: null };
  const o = raw as { data?: T[]; pagination?: V2Pagination };
  return {
    items: Array.isArray(o.data) ? o.data : [],
    pagination: o.pagination ?? null,
  };
}

/** Statuts Prisma `TrainingCampaign` → libellés UI (types v2 front). */
export function mapCampaignStatusForUi(raw: string | undefined | null): string {
  const s = String(raw || '');
  const m: Record<string, string> = {
    campaign_draft: 'draft',
    scheduled: 'scheduled',
    campaign_active: 'active',
    paused: 'paused',
    campaign_completed: 'completed',
    cancelled: 'cancelled',
  };
  return m[s] || s || 'draft';
}

export const v2ReportingApi = {
  dashboard: () => api.get<unknown>('/v2/reporting/dashboard').then(unwrapV2Data),
  kpis: () => api.get<unknown>('/v2/reporting/dashboard/kpis').then(unwrapV2Data),
  summary: () => api.get<unknown>('/v2/reporting/dashboard/summary').then(unwrapV2Data),
  trendsCompletion: (months?: number) =>
    api.get<unknown>('/v2/reporting/trends/completion', { months: months ?? 12 }).then(unwrapV2Data),
  trendsBudget: (years?: number) =>
    api.get<unknown>('/v2/reporting/trends/budget', { years: years ?? 3 }).then(unwrapV2Data),
  budgetByDepartment: (year?: number) =>
    api
      .get<unknown>('/v2/reporting/budget/by-department', { year: year ?? new Date().getFullYear() })
      .then(unwrapV2Data),
  budgetForecast: () => api.get<unknown>('/v2/reporting/budget/forecast').then(unwrapV2Data),
};

export const v2CampaignsApi = {
  stats: () => api.get<unknown>('/v2/campaigns/stats').then(unwrapV2Data),
  list: (params: { page?: number; limit?: number; search?: string; status?: string }) =>
    api.get<unknown>('/v2/campaigns', params).then(unwrapV2Paginated),
  get: (id: string) => api.get<unknown>(`/v2/campaigns/${id}`).then(unwrapV2Data),
  create: (body: Record<string, unknown>) => api.post<unknown>('/v2/campaigns', body).then(unwrapV2Data),
  update: (id: string, body: Record<string, unknown>) => api.put<unknown>(`/v2/campaigns/${id}`, body).then(unwrapV2Data),
  remove: (id: string) => api.delete<unknown>(`/v2/campaigns/${id}`),
  launch: (id: string) => api.post<unknown>(`/v2/campaigns/${id}/launch`, {}).then(unwrapV2Data),
  complete: (id: string) => api.post<unknown>(`/v2/campaigns/${id}/complete`, {}).then(unwrapV2Data),
  cancel: (id: string) => api.post<unknown>(`/v2/campaigns/${id}/cancel`, {}).then(unwrapV2Data),
  analytics: (id: string) => api.get<unknown>(`/v2/campaigns/${id}/analytics`).then(unwrapV2Data),
  participants: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<unknown>(`/v2/campaigns/${id}/participants`, params ?? {}).then(unwrapV2Paginated),
  assign: (
    id: string,
    body: { userIds?: string[]; learningGroupIds?: string[]; serviceIds?: string[] },
  ) => api.post<unknown>(`/v2/campaigns/${id}/assign`, body).then(unwrapV2Data),
  assignableUsers: (params?: { search?: string; limit?: number }) =>
    api.get<unknown>('/v2/campaigns/assignable-users', params ?? {}).then(unwrapV2Data),
};

export const v2CatalogApi = {
  categories: () => api.get<unknown>('/v2/catalog/categories').then(unwrapV2Data),
  listItems: (params: {
    page?: number;
    limit?: number;
    search?: string;
    categoryId?: string;
    published?: string;
    /** Inclure les parcours EduFlow publiés des formateurs de l’établissement (défaut côté API si omis). */
    includeFlows?: string;
  }) => api.get<unknown>('/v2/catalog/items', params).then(unwrapV2Paginated),
  getItem: (id: string) => api.get<unknown>(`/v2/catalog/items/${id}`).then(unwrapV2Data),
  popular: (limit?: number) =>
    api.get<unknown>('/v2/catalog/popular', { limit: limit ?? 12 }).then(unwrapV2Data),
  enroll: (id: string) => api.post<unknown>(`/v2/catalog/items/${id}/enroll`, {}).then(unwrapV2Data),
};

export const v2CompetenciesApi = {
  domains: () => api.get<unknown>('/v2/competencies/domains').then(unwrapV2Data),
  /** Création référentiel (droits équivalent `/v2/competencies` RH). */
  createDomain: (body: { name: string; description?: string }) =>
    api.post<unknown>('/v2/competencies/domains', body).then(unwrapV2Data),
  createFamily: (body: { domainId: string; name: string; description?: string }) =>
    api.post<unknown>('/v2/competencies/families', body).then(unwrapV2Data),
  createCompetency: (body: {
    familyId: string;
    name: string;
    description?: string;
    maxLevel?: number;
  }) => api.post<unknown>('/v2/competencies', body).then(unwrapV2Data),
  deleteDomain: (id: string) => api.delete<unknown>(`/v2/competencies/domains/${id}`).then(unwrapV2Data),
  deleteFamily: (id: string) => api.delete<unknown>(`/v2/competencies/families/${id}`).then(unwrapV2Data),
  deleteCompetency: (id: string) => api.delete<unknown>(`/v2/competencies/${id}`).then(unwrapV2Data),
  /** Arbre domaines → familles → compétences (niveaux attendus, descripteurs de niveaux). */
  framework: () => api.get<unknown>('/v2/competencies/framework').then(unwrapV2Data),
  /** Moyennes par équipe/service (libellé `User.department`) × compétence (évals validées). */
  heatmap: () => api.get<unknown>('/v2/competencies/heatmap').then(unwrapV2Data),
  gapCritical: () => api.get<unknown>('/v2/competencies/gap-analysis/critical').then(unwrapV2Data),
  gapAnalysis: (params?: { userId?: string; departmentLabel?: string }) =>
    api.get<unknown>('/v2/competencies/gap-analysis', params ?? {}).then(unwrapV2Data),
};

export const v2ManagerApi = {
  team: () => api.get<unknown>('/v2/manager/team').then(unwrapV2Data),
};

export const v2WorkflowsApi = {
  listRequests: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get<unknown>('/v2/workflows/requests', params ?? {}).then(unwrapV2Paginated),
};

export type V2TrainingContentRequestRow = {
  id: string;
  institutionId: string;
  templateId: string;
  requesterId: string;
  type: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  data: Record<string, unknown>;
  requester?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
};

export const v2TrainingContentRequestsApi = {
  list: (params?: { status?: string }) =>
    api.get<unknown>('/v2/training-content-requests', params ?? {}).then(unwrapV2Data) as Promise<
      V2TrainingContentRequestRow[]
    >,
  create: (body: Record<string, unknown>) =>
    api.post<unknown>('/v2/training-content-requests', body).then(unwrapV2Data),
  approve: (id: string) =>
    api.post<unknown>(`/v2/training-content-requests/${id}/approve`, {}).then(unwrapV2Data),
  reject: (id: string, body?: { reason?: string }) =>
    api.post<unknown>(`/v2/training-content-requests/${id}/reject`, body ?? {}).then(unwrapV2Data),
};

/** Réponses `/v2/compliance/*` (Qualiopi RNQ, données Prisma). */
export type V2QualiopiDashboardCriterion = {
  id: string;
  number: number;
  title: string;
  complianceRate: number;
  indicatorCount: number;
  conformeCount?: number;
  enCoursCount?: number;
  nonConformeCount?: number;
};

export type V2QualiopiDashboard = {
  overallCompliance: number;
  nextAuditDate: string | null;
  criteria: V2QualiopiDashboardCriterion[];
};

export type V2QualiopiCriterionDetail = {
  id: string;
  number: number;
  title: string;
  description: string;
  completionPercentage: number;
  indicators: Array<{
    id: string;
    number: string;
    title: string;
    description: string;
    status: 'compliant' | 'partial' | 'non_compliant' | 'not_applicable';
    notes: string;
    lastAuditDate: string | null;
    nextReviewDate: string | null;
    evidenceDocuments: Array<{
      id: string;
      name: string;
      type: string;
      url: string;
      uploadedAt: string;
      retentionUntil?: string | null;
      status: 'valid' | 'pending' | 'expired';
      uploadedBy?: string;
    }>;
    actionItems: Array<{ id: string; title: string; dueDate: string; completed: boolean }>;
  }>;
};

export type V2RegulatoryWatchAckStatus = 'pending' | 'read' | 'action_taken' | 'not_applicable';

export type V2RegulatoryWatchItem = {
  id: string;
  date: string;
  category: 'reglementaire' | 'pedagogique' | 'technique' | 'sectoriel';
  title: string;
  description: string;
  source: string;
  impact: 'fort' | 'moyen' | 'faible';
  read: boolean;
  actionTitle?: string | null;
  actionDeadline?: string | null;
  actionAssignee?: string | null;
  actionPriority?: 'haute' | 'moyenne' | 'basse' | null;
  actionDone: boolean;
  /** institution = saisie OF ; lyrra_global = alerte plateforme */
  origin?: string;
  globalBroadcastId?: string | null;
  institutionResponse?: string | null;
  acknowledgementStatus?: V2RegulatoryWatchAckStatus | null;
  createdAt: string;
  updatedAt: string;
};

export type V2SatisfactionAutomationRule = {
  id: string;
  institutionId: string;
  trigger: 'eduflow_completed' | 'learning_path_completed';
  sourceEduFlowId: string | null;
  sourceLearningPathId: string | null;
  surveyFlowId: string;
  daysAfterCompletion: number;
  surveyType: 'hot' | 'cold';
  isActive: boolean;
  emailSubject: string | null;
  createdAt: string;
  updatedAt: string;
  sourceEduFlow: { id: string; title: string } | null;
  sourceLearningPath: { id: string; title: string } | null;
  surveyEduFlow: { id: string; title: string; shortCodePublic?: string | null; status?: string };
};

export type V2SatisfactionAutomationQueueRow = {
  id: string;
  notifyEmail: string;
  sourceTitle: string | null;
  surveyFlowId: string;
  completedAt: string;
  sendAt: string;
  sentAt: string | null;
  lastError: string | null;
  attempts: number;
  abandonedAt: string | null;
  rule: { id: string; trigger: string; daysAfterCompletion: number };
};

/** URL absolue pour un chemin `/uploads/...` servi par le backend. */
export function resolveQualiopiEvidenceUrl(storedUrl: string): string {
  const normalized = normalizeUploadsUrlPath(storedUrl);
  if (/^https?:\/\//i.test(normalized)) return normalized;
  const origin = backendOriginWithoutApi(config.API_URL);
  return `${origin.replace(/\/$/, '')}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
}

export const v2ComplianceApi = {
  syncFramework: () => api.post<unknown>('/v2/compliance/framework/sync', {}).then(unwrapV2Data),
  dashboard: () =>
    api.get<unknown>('/v2/compliance/dashboard').then((raw) => unwrapV2Data<V2QualiopiDashboard>(raw)),
  getCriterion: (id: string) =>
    api.get<unknown>(`/v2/compliance/criteria/${id}`).then((raw) => unwrapV2Data<V2QualiopiCriterionDetail>(raw)),
  updateIndicator: (
    id: string,
    body: { status?: V2QualiopiCriterionDetail['indicators'][0]['status']; notes?: string | null },
  ) => api.put<unknown>(`/v2/compliance/indicators/${id}`, body).then(unwrapV2Data),
  satisfactionMetrics: () => api.get<unknown>('/v2/compliance/satisfaction-metrics').then(unwrapV2Data),
  satisfactionAutomationOptions: () =>
    api
      .get<unknown>('/v2/compliance/satisfaction-automation/options')
      .then((raw) =>
        unwrapV2Data<{
          flows: Array<{ id: string; title: string; shortCodePublic: string | null }>;
          paths: Array<{ id: string; title: string }>;
        }>(raw),
      ),
  satisfactionAutomationRules: () =>
    api.get<unknown>('/v2/compliance/satisfaction-automation/rules').then((raw) => unwrapV2Data<V2SatisfactionAutomationRule[]>(raw)),
  createSatisfactionAutomationRule: (body: {
    trigger: 'eduflow_completed' | 'learning_path_completed';
    sourceEduFlowId?: string | null;
    sourceLearningPathId?: string | null;
    surveyFlowId: string;
    daysAfterCompletion: number;
    surveyType?: 'hot' | 'cold';
    isActive?: boolean;
    emailSubject?: string | null;
  }) =>
    api.post<unknown>('/v2/compliance/satisfaction-automation/rules', body).then((raw) => unwrapV2Data<V2SatisfactionAutomationRule>(raw)),
  updateSatisfactionAutomationRule: (
    id: string,
    patch: Partial<{
      daysAfterCompletion: number;
      isActive: boolean;
      emailSubject: string | null;
      surveyFlowId: string;
      surveyType: 'hot' | 'cold';
    }>,
  ) => api.patch<unknown>(`/v2/compliance/satisfaction-automation/rules/${id}`, patch).then((raw) => unwrapV2Data<V2SatisfactionAutomationRule>(raw)),
  deleteSatisfactionAutomationRule: (id: string) =>
    api.delete<unknown>(`/v2/compliance/satisfaction-automation/rules/${id}`).then(unwrapV2Data),
  satisfactionAutomationQueue: (limit = 40) =>
    api
      .get<unknown>('/v2/compliance/satisfaction-automation/queue', { limit })
      .then((raw) => unwrapV2Data<V2SatisfactionAutomationQueueRow[]>(raw)),
  auditLogs: (limit = 100) =>
    api.get<unknown>('/v2/compliance/audit-logs', { limit }).then(unwrapV2Data),
  getHandicapReferent: () => api.get<unknown>('/v2/compliance/handicap-referent').then(unwrapV2Data),
  upsertHandicapReferent: (body: {
    name: string;
    email: string;
    phone: string;
    role?: string | null;
    supportHours?: string | null;
    notes?: string | null;
    accommodations?: string[];
  }) => api.put<unknown>('/v2/compliance/handicap-referent', body).then(unwrapV2Data),
  uploadEvidenceFile: (indicatorId: string, file: File, opts?: { title?: string; description?: string; retentionYears?: number }) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts?.title) fd.append('title', opts.title);
    if (opts?.description) fd.append('description', opts.description);
    if (opts?.retentionYears != null) fd.append('retentionYears', String(opts.retentionYears));
    return api.upload<unknown>(`/v2/compliance/indicators/${indicatorId}/evidences/upload`, fd).then(unwrapV2Data);
  },
  createCorrectiveAction: (indicatorId: string, body: { title: string; dueDate?: string | null }) =>
    api.post<unknown>(`/v2/compliance/indicators/${indicatorId}/actions`, body).then(unwrapV2Data),
  updateCorrectiveAction: (
    indicatorId: string,
    actionId: string,
    body: { title?: string; dueDate?: string | null; completed?: boolean },
  ) => api.put<unknown>(`/v2/compliance/indicators/${indicatorId}/actions/${actionId}`, body).then(unwrapV2Data),
  deleteCorrectiveAction: (indicatorId: string, actionId: string) =>
    api.delete<unknown>(`/v2/compliance/indicators/${indicatorId}/actions/${actionId}`).then(unwrapV2Data),
  regulatoryWatch: (category: 'tous' | V2RegulatoryWatchItem['category'] = 'tous') =>
    api
      .get<unknown>('/v2/compliance/regulatory-watch', category === 'tous' ? {} : { category })
      .then((raw) => unwrapV2Data<V2RegulatoryWatchItem[]>(raw)),
  createRegulatoryWatchItem: (body: {
    date: string;
    category: V2RegulatoryWatchItem['category'];
    title: string;
    description: string;
    source: string;
    impact: V2RegulatoryWatchItem['impact'];
    actionTitle?: string | null;
    actionDeadline?: string | null;
    actionAssignee?: string | null;
    actionPriority?: NonNullable<V2RegulatoryWatchItem['actionPriority']>;
  }) => api.post<unknown>('/v2/compliance/regulatory-watch', body).then((raw) => unwrapV2Data<V2RegulatoryWatchItem>(raw)),
  updateRegulatoryWatchItem: (
    id: string,
    patch: Partial<{
      read: boolean;
      actionDone: boolean;
      title: string;
      description: string;
      source: string;
      impact: V2RegulatoryWatchItem['impact'];
      category: V2RegulatoryWatchItem['category'];
      date: string;
      actionTitle: string | null;
      actionDeadline: string | null;
      actionAssignee: string | null;
      actionPriority: V2RegulatoryWatchItem['actionPriority'];
      institutionResponse: string | null;
      acknowledgementStatus: V2RegulatoryWatchAckStatus;
    }>,
  ) => api.patch<unknown>(`/v2/compliance/regulatory-watch/${id}`, patch).then((raw) => unwrapV2Data<V2RegulatoryWatchItem>(raw)),
  deleteRegulatoryWatchItem: (id: string) =>
    api.delete<unknown>(`/v2/compliance/regulatory-watch/${id}`).then(unwrapV2Data),
};
